// Some re-usable simple schema type definitions.
// NOTE: Never mutate these objects!

import {
  CoreSchemaMetaSchema,
  CoreSchemaMetaSchemaObject,
} from '@sapappgyver/appgyver-descriptors/lib/types/cloud-descriptor/v1';
import { getUnionSchema, Schema as AppGyverSchema } from '@sapappgyver/appgyver-schemas';
import { mapValues } from 'lodash';

export const STRING_WILDCARD_SCHEMA = { type: 'string', format: '*' } as const;
export const STRING_UNFORMATTED_SCHEMA = { type: 'string' } as const;
export const STRING_NUMBER_SCHEMA = { type: 'string', format: 'number' } as const;
export const STRING_PERCENTAGE_SCHEMA = { type: 'string', format: 'percentage' } as const;
export const STRING_COLOR_SCHEMA = { type: 'string', format: 'color' } as const;
export const NUMBER_SCHEMA = { type: 'number' } as const;
export const BOOLEAN_SCHEMA = { type: 'boolean' } as const;
export const ARRAY_SCHEMA = { type: 'array' } as const;
export const OBJECT_SCHEMA = { type: 'object' } as const;
export const ARRAY_OF_OBJECTS_SCHEMA = { ...ARRAY_SCHEMA, items: { ...OBJECT_SCHEMA } } as const;
export const ANY_SCHEMA = { type: 'value' } as const;
export const JSON_SCHEMA = { type: 'json' } as const;
export const UNKNOWN_SCHEMA = {} as const;
export const APP_VARIABLE_REFERENCE_SCHEMA = { type: 'appVariableRef' } as const;
export const PAGE_VARIABLE_REFERENCE_SCHEMA = { type: 'pageVariableRef' } as const;
export const PAGE_PARAMETER_REFERENCE_SCHEMA = { type: 'parameterRef' } as const;
export const CLOUD_FUNCTION_REFERENCE_SCHEMA = { type: 'cloudFunctionRef' } as const;
export const DATA_VARIABLE_REFERENCE_SCHEMA = { type: 'dataReferenceName' } as const;
export const DATA_RESOURCE_REFERENCE_SCHEMA = { type: 'resourceName' } as const;
export const PAGE_ID_REFERENCE_SCHEMA = { type: 'page' } as const;
export const COMPONENT_ID_REFERENCE_SCHEMA = { type: 'componentId' } as const;
export const SERVICE_REFERENCE_SCHEMA = { type: 'service' } as const;
export const EXTERNAL_RESOURCE_REFERENCE_SCHEMA = { type: 'externalResource' } as const;
export const SPA_PROCESS_DEFINITION_REFERENCE_SCHEMA = { type: 'spaProcessDefinitionRef' } as const;
export const BUILD_ACTION_REFERENCE_SCHEMA = { type: 'buildActionRef' } as const;
export const BTP_DESTINATION_REFERENCE_SCHEMA = { type: 'btpDestinationRef' } as const;
export const HTTP_REQUEST_METHOD_REFERENCE_SCHEMA = { type: 'httpRequestMethodRef' } as const;
export const HTTP_REQUEST_BODY_REFERENCE_SCHEMA = { type: 'httpRequestBodyRef' } as const;

export const NUMBER_FORMAT_SCHEMA = {
  anyOf: [NUMBER_SCHEMA, STRING_NUMBER_SCHEMA],
} as AppGyverSchema;

/**
 * Takes a valid JSONSchema structure and converts it to AppGyver schema.
 * @param schema valid JSONSchema structure
 * @returns AppGyver schema structure
 */
export function convertJsonSchemaToAppgyverSchema(schema: CoreSchemaMetaSchema): AppGyverSchema {
  if (typeof schema !== 'object' || schema == null) {
    throw new Error('Cannot convert non-object JSONSchema to AppGyver schema');
  }
  if (Array.isArray(schema.type)) {
    const types = schema.type;
    // Convert array of types to an union schema
    return (
      getUnionSchema(
        types.map((type) =>
          convertJsonSchemaToAppgyverSchema({
            ...schema,
            type,
          }),
        ),
      ) ?? {}
    );
  }
  const result: Record<string, any> = { ...schema };
  if (result.type === 'integer') {
    // Convert `integer` type to `number`
    result.type = 'number';
  }
  if (typeof result.const !== 'undefined') {
    // Convert `const` to `enum`
    result.enum = [result.const];
    delete result.const;
  }
  if (result.type === 'object' && result.properties && !result.required) {
    // Missing `required` in object types means different thing
    result.required = [];
  }
  if (Array.isArray(result.anyOf)) {
    // Convert sub-schemas in union types and filter out null types
    const anyOf = result.anyOf.map(convertJsonSchemaToAppgyverSchema).filter((subSchema) => subSchema.type !== 'null');
    if (anyOf.length === 1) {
      delete result.anyOf;
      Object.assign(result, anyOf[0]);
    } else {
      result.anyOf = anyOf;
    }
  }
  if (Array.isArray(result.allOf)) {
    // Convert sub-schemas in intersection types and filter out null types
    const allOf = result.allOf.map(convertJsonSchemaToAppgyverSchema).filter((subSchema) => subSchema.type !== 'null');
    if (allOf.length === 1) {
      delete result.allOf;
      Object.assign(result, allOf[0]);
    } else {
      result.allOf = allOf;
    }
  }
  if (typeof result.properties === 'object' && result.properties != null) {
    result.properties = mapValues(result.properties, convertJsonSchemaToAppgyverSchema);
  }
  if (typeof result.additionalProperties === 'object' && result.additionalProperties != null) {
    result.additionalProperties = convertJsonSchemaToAppgyverSchema(result.additionalProperties);
  }
  if (typeof result.items === 'object' && result.items != null) {
    result.items = convertJsonSchemaToAppgyverSchema(result.items);
  }
  return result;
}

/**
 * Takes a valid AppGyver schema and converts it to JSONSchema structure.
 * NOTE: That some AppGyver schema-specific types or other features cannot be converted!
 * @param schema valid AppGyver schema structure
 * @returns JSONSchema structure
 */
export function convertAppgyverSchemaToJsonSchema(schema: AppGyverSchema): CoreSchemaMetaSchemaObject {
  const result: Record<string, any> = { ...schema };
  if (result.type === 'object' && result.properties && !result.required) {
    // Missing `required` in object types means all known properties are required!
    result.required = Object.keys(result.properties);
  }
  // Convert type
  switch (result.type) {
    case null:
    case undefined:
    case 'json':
    case 'value':
      // Allow any value
      delete result.type;
      break;
    case 'string':
    case 'number':
    case 'integer':
    case 'boolean':
    case 'object':
    case 'array':
      // Allow null values with all of these types
      result.type = [result.type, 'null'];
      break;
    case 'null':
      // Keep type as-is
      break;
    case 'undefined':
      // JSONSchema does not have this as a type.
      // Convert to "unkown" schema.
      delete result.type;
      break;
    case 'appVariableRef':
    case 'pageVariableRef':
    case 'parameterRef':
    case 'cloudFunctionRef':
    case 'dataReferenceName':
    case 'resourceName':
    case 'page':
    case 'componentId':
    case 'service':
    case 'externalResource':
    case 'fontFamily':
    case 'supportedLanguage':
    case 'spaProcessDefinitionRef':
    case 'buildActionRef':
    case 'btpDestinationRef':
    case 'httpRequestBodyRef':
    case 'httpRequestMethodRef':
      // Special references with string values
      result.type = ['string', 'null'];
      break;
    case 'appVariableAssignment':
    case 'pageVariableAssignment':
    case 'dataReferenceAssignment':
    case 'componentDataReferenceAssignment':
    case 'componentPropertyAssignment':
    case 'internalPropertyAssignment':
    case 'outputValueAssignment':
    case 'privateVariableAssignment':
    case 'privateDataVariableAssignment':
    case 'componentProperty':
    case 'repeatedItemAssignment':
    case 'pageNavigation':
    case 'dataResourceRetrieveRequest':
    case 'dataResourceListRequest':
    case 'dataResourceCreateRequest':
    case 'dataResourceReplaceRequest':
    case 'dataResourceDeleteRequest':
    case 'dataResourceRetrieveResponse':
    case 'dataResourceListResponse':
    case 'dataResourceCreateResponse':
    case 'dataResourceReplaceResponse':
    case 'dataResourceDeleteResponse':
      // Special object constructs
      result.type = ['object', 'null'];
      break;
    case 'schema':
    case 'schematicValue':
      // Special object construct
      result.type = ['object', 'null'];
      delete result.schema;
      break;
    default:
      throw new Error(`Cannot convert AppGyver schema type ${JSON.stringify(result.type)}`);
  }
  // Ignore special wildcard format
  if (result.format === '*') {
    delete result.format;
  }
  if (Array.isArray(result.anyOf)) {
    // Convert sub-schemas in union types
    result.anyOf = result.anyOf.map(convertAppgyverSchemaToJsonSchema);
  }
  if (Array.isArray(result.allOf)) {
    // Convert sub-schemas in intersection types
    result.allOf = result.allOf.map(convertAppgyverSchemaToJsonSchema);
  }
  if (typeof result.properties === 'object' && result.properties != null) {
    result.properties = mapValues(result.properties, convertAppgyverSchemaToJsonSchema);
  }
  if (typeof result.additionalProperties === 'object' && result.additionalProperties != null) {
    result.additionalProperties = convertAppgyverSchemaToJsonSchema(result.additionalProperties);
  }
  if (typeof result.items === 'object' && result.items != null) {
    result.items = convertAppgyverSchemaToJsonSchema(result.items);
  }
  return result;
}
